import * as React from 'react';

import { RecommendationsList } from '../../components/RecommendationsList';
import { RecommendationsListDesktop } from '../../components/RecommendationsListDesktop';
import { useRecommendations } from './useRecommendations';
import { ELoadingStatus } from '../../types/loadingStatus';
import { RecommendationSkeleton } from '../../components/Recommendation';
import { TRecommendation } from '../../types/recommendation';
import { RecommendationDesktop, RecommendationDesktopSkeleton } from '../../components/RecommendationDesktop';
import { FavoriteButtonContainer } from '../FavoriteButtonContainer';
import { LabelsContainer } from '../LabelsContainer';
import { trackEntityTeaserShow } from './tracking';
import { RecommendationsListMobile } from './RecommendationsListMobile';
import { RecommendationsListDesktopWithNewbuildings } from '../../components/RecommendationsListDesktopWithNewbuildings';
import { NewbuildingRecommendations } from '../NewbuildingRecommendations/NewbuildingRecommendations';
import { useSelector } from 'react-redux';
import { selectIsIndexLandingType, selectLandingType } from '../../selectors/landingType';
import { useApplicationContext } from '../../utils/applicationContext';
import { EQueryParameters as ELandingType } from '../../repositories/feed-page/v2/mobile-site/get-recommendations';

export function RecommendationsListContainer() {
  const { config } = useApplicationContext();
  const recommendationsControls = useRecommendations();
  const landingType = useSelector(selectLandingType);
  const showRecommendations =
    useSelector(selectIsIndexLandingType) || config.get('NewbuildingRecommendations.ShowOnVerticals.Enabled');
  const infiniteScroll = landingType !== ELandingType.FlatRentForDay;
  const {
    recommendations,
    onRecommendationClick,
    onShow,
    onShowTrap,
    showHeader,
    recommendationsStatus,
    isMobile,
    regionId,
    onMoreClick,
    isOfferVisited,
    showFullList,
  } = recommendationsControls;

  const recommendationsMobileSkeleton = React.useMemo(
    () =>
      new Array(18)
        .fill(0)
        .map((_, index) => <RecommendationSkeleton key={`recommendation-mobile-skeleton-${index}`} />),
    [],
  );

  const DESKTOP_SKELETON_COUNT = showFullList ? 40 : 8;

  const recommendationsDesktopSkeleton = React.useMemo(
    () =>
      new Array(DESKTOP_SKELETON_COUNT)
        .fill(0)
        .map((_, index) => <RecommendationDesktopSkeleton key={`recommendation-desktop-skeleton-${index}`} />),
    [DESKTOP_SKELETON_COUNT],
  );

  const handleEntityTrackTeaserShow = React.useCallback(
    (offerId: number | null, position: number, contextToken: string) => {
      if (!offerId) {
        return;
      }

      trackEntityTeaserShow({ offerId, position, contextToken }, isMobile);
    },
    [isMobile],
  );

  if (recommendationsStatus === ELoadingStatus.Loading) {
    return isMobile ? (
      <RecommendationsList withHeader={showHeader}>{recommendationsMobileSkeleton}</RecommendationsList>
    ) : (
      <RecommendationsListDesktop showMoreLink={!showFullList}>
        {recommendationsDesktopSkeleton}
      </RecommendationsListDesktop>
    );
  }

  if (!recommendations.length) {
    return null;
  }

  return isMobile ? (
    <RecommendationsListMobile
      recommendationsControls={recommendationsControls}
      onTrack={handleEntityTrackTeaserShow}
      infiniteScroll={infiniteScroll}
    />
  ) : !showRecommendations ? (
    <RecommendationsListDesktop onShow={onShow} onMoreClick={onMoreClick} showMoreLink={!showFullList}>
      {recommendations.map((recommendation: TRecommendation, index) => (
        <RecommendationDesktop
          onTrack={handleEntityTrackTeaserShow}
          position={index + 1}
          key={recommendation.offerId}
          onClick={onRecommendationClick}
          onShowTrap={onShowTrap}
          regionId={regionId}
          favoriteButton={<FavoriteButtonContainer size="M" id={recommendation.offerId} />}
          labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
          {...recommendation}
        />
      ))}
    </RecommendationsListDesktop>
  ) : (
    <RecommendationsListDesktopWithNewbuildings
      showMoreLink={!showFullList}
      secondRow={<NewbuildingRecommendations />}
      onShow={onShow}
      onMoreClick={onMoreClick}
    >
      {recommendations.map((recommendation: TRecommendation, index) => (
        <RecommendationDesktop
          onTrack={handleEntityTrackTeaserShow}
          position={index + 1}
          key={recommendation.offerId}
          onClick={onRecommendationClick}
          onShowTrap={onShowTrap}
          regionId={regionId}
          favoriteButton={<FavoriteButtonContainer size="M" id={recommendation.offerId} />}
          labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
          {...recommendation}
        />
      ))}
    </RecommendationsListDesktopWithNewbuildings>
  );
}
